import React from "react";
import { CheckCircle, Clock, AlertTriangle } from "lucide-react";

/**
 * PhaseBreakdownTable
 *
 * Props:
 * - byPhase: {
 *     [phaseName]: {
 *       id: string
 *       total: number
 *       completed: number
 *       pending: number
 *       overdue: number
 *     }
 *   }
 */
export default function PhaseBreakdownTable({ byPhase }) {
  if (!byPhase || Object.keys(byPhase).length === 0) {
    return (
      <div className="rounded-xl border bg-white p-6 text-sm text-gray-500">
        No phase data available for this project.
      </div>
    );
  }

  const rows = Object.entries(byPhase);

  const badge = (Icon, value, color) => (
    <div className={`inline-flex items-center gap-1 text-sm ${color}`}>
      <Icon className="w-4 h-4" />
      {value}
    </div>
  );

  return (
    <div className="rounded-xl border bg-white overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b">
        <h2 className="text-lg font-semibold text-gray-900">
          Phase Breakdown
        </h2>
        <p className="text-xs text-gray-500">
          Task distribution across project phases
        </p>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-6 py-3 text-left font-medium">Phase</th>
              <th className="px-4 py-3 text-center font-medium">Total</th>
              <th className="px-4 py-3 text-center font-medium">Completed</th>
              <th className="px-4 py-3 text-center font-medium">Pending</th>
              <th className="px-4 py-3 text-center font-medium">Overdue</th>
            </tr>
          </thead>

          <tbody className="divide-y">
            {rows.map(([phaseName, stats]) => (
              <tr key={stats.id || phaseName} className="hover:bg-gray-50">
                <td className="px-6 py-3 font-medium text-gray-900">
                  {phaseName}
                </td>

                <td className="px-4 py-3 text-center">
                  {stats.total ?? 0}
                </td>

                <td className="px-4 py-3 text-center">
                  {badge(
                    CheckCircle,
                    stats.completed ?? 0,
                    "text-emerald-600"
                  )}
                </td>

                <td className="px-4 py-3 text-center">
                  {badge(
                    Clock,
                    stats.pending ?? 0,
                    "text-amber-600"
                  )}
                </td>

                <td className="px-4 py-3 text-center">
                  {badge(
                    AlertTriangle,
                    stats.overdue ?? 0,
                    "text-rose-600"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}